import React, { useState, useEffect, useMemo, useRef } from "react";
import "../styles/Wallet.css";
import "../styles/BuyCredits.css";
import XIcon from "../icons/XIcon.js";

import StarterIcon from "../icons/BusinessHub/StarterIcon";
import PopulariIcon from "../icons/BusinessHub/PopularIcon";
import ProIcon from "../icons/BusinessHub/ProIcon";
import ProductBoostIcon from "../icons/BusinessHub/ProductBoost";
import SponsoredAdIcon from "../icons/BusinessHub/SponsoredAd";
import PriorityListingIcon from "../icons/BusinessHub/PriorityListing";
import HomePageFeatureIcon from "../icons/BusinessHub/HomePageFeature";
import WalletIcon from "../icons/Wallet";
import CheckIcon from "../icons/BusinessHub/CheckIcon";
import Utils from "../utils/Utils";
import { userService } from "../services/userService";
import { toast } from "react-toastify";
import CreditsController from "../controllers/CreditsController.js";
import PromotionController from "../controllers/PromotionController.js";
import BlomBanckController from "../controllers/BlomBanckController.js";
import companyController from "../controllers/companyController.js";

import WalletIconBig from "../icons/WalletIconBig.js";
import SearchIcon from "../icons/BusinessHub/SearchIcon.js";
import FilterIcon from "../icons/BusinessHub/FilterIcon.js";
import TotalWalletBalanceIcon from "../icons/TotalWalletBalanceIcon";
import PendingSettlementsIcon from "../icons/PendingSettlementsIcon";
import ActivePlatformCreditsIcon from "../icons/ActivePlatformCreditsIcon";
import ExportIcon from "../icons/BusinessHub/ExportIcon.js";
import ChaseBankIcon from "../icons/ChaseBankIcon.js";
import VisaIcon from "../icons/VIsaIcon.js";
import ArrowDownIcon from "../icons/BusinessHub/ArrowDownIcon.js";
import DateRangePicker from "./DateRangePicker.js";
import CustomDropdown from "./StatusDropdown.js";
import ArrowFront from "../icons/BusinessHub/ArrowFront.js";
import ArrowBackIcon from "../icons/BusinessHub/ArrowBackIcon.js";

const walletTypeOptions = [
  { label: "Vendor Admin", value: "VENDOR_ADMIN" },
  { label: "Azalea Admin", value: "AZALEA_ADMIN" },
  { label: "Delivery", value: "DELIVERY" },
];

const walletStatusOptions = [
  { label: "Completed", value: "COMPLETED" },
  { label: "Pending", value: "PENDING" },
];

const emptyWalletFilters = {
  walletType: [],
  walletStatus: "",
};

const emptyAddMethodForm = {
  nameOnCard: "",
  cardNumber: "",
  expiry: "",
  cvv: "",
};

const formatWalletDate = (value) => {
  if (!value) return "-";

  const date =
    typeof value === "object" && value.time !== undefined
      ? new Date(value.time)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatWalletAmount = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (typeof value === "string" && /^[+-]?\$/.test(value.trim())) {
    return value;
  }

  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[$,]/g, ""));

  if (!Number.isFinite(numeric)) return value;

  const sign = numeric > 0 ? "+" : numeric < 0 ? "-" : "";
  return `${sign}${Math.abs(numeric).toFixed(2)}`;
};

const getWalletRowValue = (row, keys, fallback = "-") => {
  const key = keys.find((candidate) => row?.[candidate] !== undefined);
  return key ? row[key] : fallback;
};

const getWalletTransactionRowId = (row, index) =>
  String(
    row?.walletTransactionId ??
      row?.transactionId ??
      row?.referenceId ??
      `wallet-transaction-${index}`,
  );

const getWalletCreditRowId = (row, index) =>
  String(
    row?.walletTransactionId ??
      row?.creditTransactionId ??
      row?.orderReference ??
      `wallet-credit-${index}`,
  );

const formatMoney = (value) => {
  return `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getCurrentUserId = (decodedUser) => {
  const userDetails = decodedUser?.userDetails
    ? Object.values(decodedUser.userDetails)?.[0]
    : null;

  return (
    decodedUser?.id ??
    decodedUser?.userId ??
    userDetails?.employeeId ??
    userDetails?.userId ??
    ""
  );
};

const getFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeCountryCode = (value) => {
  const country = String(value || "").trim();
  if (!country) return "LB";
  if (country.length === 2) return country.toUpperCase();
  if (country.toLowerCase().includes("leban")) return "LB";
  return country.slice(0, 2).toUpperCase();
};

const formatExpiryForApi = (expiry) => {
  const [month, year] = String(expiry || "").split("/");
  if (!month || !year) return "";

  const fullYear = 2000 + Number(year);
  const lastDay = new Date(fullYear, Number(month), 0).getDate();

  return `${fullYear}/${month}/${lastDay} 23:59:59`;
};

const maskCardNumber = (cardNumber) => {
  const digits = String(cardNumber || "").replace(/\D/g, "");
  const lastFour = digits.slice(-4);

  return lastFour ? `•••• ${lastFour}` : "••••";
};

const isValidLuhn = (cardNumber) => {
  let sum = 0;
  let shouldDouble = false;

  for (let i = cardNumber.length - 1; i >= 0; i -= 1) {
    let digit = Number(cardNumber[i]);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

const isExpiryValid = (expiry) => {
  const [monthText, yearText] = String(expiry || "").split("/");
  if (!monthText || !yearText || yearText.length !== 2) return false;

  const month = Number(monthText);
  const year = 2000 + Number(yearText);

  if (month < 1 || month > 12) return false;

  const expiryDate = new Date(year, month, 0, 23, 59, 59);
  return expiryDate >= new Date();
};

const checkCreditCard = ({ cardName, cardNumber, expiry, ccv }) => {
  const digits = String(cardNumber || "").replace(/\D/g, "");
  const cleanCcv = String(ccv || "").replace(/\D/g, "");

  if (!String(cardName || "").trim()) return "Cardholder name is required.";
  if (digits.length !== 16) return "Card number must be 16 digits.";
  if (!isValidLuhn(digits)) return "Invalid card number.";
  if (!isExpiryValid(expiry)) return "Expiry date is invalid or expired.";
  if (cleanCcv.length < 3 || cleanCcv.length > 4) {
    return "CCV must be 3 or 4 digits.";
  }

  return "";
};

function Wallet() {
  const [buyCridits, setBuyCridits] = useState(false);
  const [ReqWD, setReqWD] = useState(false);
  const [ReqWithdrow, setReqWithdrow] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [activeTab, setActiveTab] = useState("transactions");
  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddMethodOpen, setIsAddMethodOpen] = useState(false);
  const [addMethodForm, setAddMethodForm] = useState(emptyAddMethodForm);
  const [isSavingMethod, setIsSavingMethod] = useState(false);
  const [draftFilters, setDraftFilters] = useState(emptyWalletFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyWalletFilters);
  const [amountSortDirection, setAmountSortDirection] = useState("");
  const [creditSortDirection, setCreditSortDirection] = useState("");
  const [search, setSearch] = useState("");
  const [selectedTransactionRows, setSelectedTransactionRows] = useState([]);
  const [selectedCreditRows, setSelectedCreditRows] = useState([]);
  const [walletRows, setWalletRows] = useState([]);
  const [walletTotalRows, setWalletTotalRows] = useState(0);
  const [walletTotalPages, setWalletTotalPages] = useState(1);
  const [creditRows, setCreditRows] = useState([]);
  const [creditsRefreshKey, setCreditsRefreshKey] = useState(0);
  const [walletOverview, setWalletOverview] = useState({
    activeCredits: 0,
    pendingSettlements: 0,
    totalWalletBalance: 0,
  });
  const [creditSummary, setCreditSummary] = useState({
    issuedThisMonth: 0,
    revenueFromCredits: 0,
    spentThisMonth: 0,
    total: 0,
    totalActiveCredits: 0,
    totalPages: 1,
  });
  // Withdrawals and billings are intentionally hidden for now, but we keep
  // placeholder values so the commented UI paths remain non-destructive.
  const withdrawalRows = [];
  const billingRows = [];
  const withdrawalCards = [];
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [billingProfile, setBillingProfile] = useState(null);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [isProcessingCreditPayment, setIsProcessingCreditPayment] =
    useState(false);
  const [resumeCreditPurchase, setResumeCreditPurchase] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [isWalletLoading, setIsWalletLoading] = useState(false);
  const [isCreditLoading, setIsCreditLoading] = useState(false);
  const isWithdrawalLoading = false;
  const isBillingLoading = false;
  const [isPaymentMethodsLoading, setIsPaymentMethodsLoading] = useState(false);
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);
  const filterPanelRef = useRef(null);
  const Authorization = userService.getUser();
  const parsed = useMemo(() => Utils.parseJwt(Authorization), [Authorization]);
  const isRoot = parsed?.isRoot;
  const currentUserId = useMemo(() => getCurrentUserId(parsed), [parsed]);
  const userObject = useMemo(() => userService.getUserObject?.() || null, []);
  const parsedFirstName = parsed?.firstName ?? parsed?.firstname;
  const parsedLastName = parsed?.lastName ?? parsed?.lastname;
  const parsedUsername = parsed?.username ?? parsed?.sub;

  const formatCreditsValue = (value) => {
    return Number(value || 0).toLocaleString();
  };

  const creditCards = isRoot
    ? [
        [
          "Total Active Credits",
          formatCreditsValue(creditSummary.totalActiveCredits),
          "Total Active Credits",
        ],
        [
          "Issued This Month",
          formatCreditsValue(creditSummary.issuedThisMonth),
          "New credits",
        ],
        [
          "Spent This Month",
          formatCreditsValue(creditSummary.spentThisMonth),
          "Platform services",
        ],
        [
          "Revenue from Credits",
          `$${Number(creditSummary.revenueFromCredits || 0).toFixed(2)}`,
          "This month",
        ],
      ]
    : [
        [
          "Current Balance",
          formatCreditsValue(creditSummary.totalActiveCredits),
          "Credits Available",
        ],
        [
          "Purchased This Month",
          formatCreditsValue(creditSummary.issuedThisMonth),
          "Credits Added",
        ],
        [
          "Spent This Month",
          formatCreditsValue(creditSummary.spentThisMonth),
          "Credits Spent",
        ],
      ];

  const displayedTransactionRows = walletRows;
  const displayedTransactionRowIds = useMemo(
    () =>
      displayedTransactionRows.map((row, index) =>
        getWalletTransactionRowId(row, index),
      ),
    [displayedTransactionRows],
  );
  const allDisplayedTransactionsSelected =
    displayedTransactionRowIds.length > 0 &&
    displayedTransactionRowIds.every((rowId) =>
      selectedTransactionRows.includes(rowId),
    );
  const displayedCreditRowIds = useMemo(
    () => creditRows.map((row, index) => getWalletCreditRowId(row, index)),
    [creditRows],
  );
  const allDisplayedCreditsSelected =
    displayedCreditRowIds.length > 0 &&
    displayedCreditRowIds.every((rowId) => selectedCreditRows.includes(rowId));
  const activeTotalRows =
    activeTab === "credits" ? creditSummary.total : walletTotalRows;
  const activeTotalPages =
    activeTab === "credits" ? creditSummary.totalPages : walletTotalPages;

  const walletPageNumbers = useMemo(() => {
    return Array.from({ length: Math.max(activeTotalPages, 1) }, (_, index) => {
      return index + 1;
    }).slice(0, 5);
  }, [activeTotalPages]);

  const toggleDraftWalletType = (walletType) => {
    setDraftFilters((prev) => ({
      ...prev,
      walletType: prev.walletType.includes(walletType)
        ? prev.walletType.filter((item) => item !== walletType)
        : [...prev.walletType, walletType],
    }));
  };

  const applyWalletFilters = () => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const clearWalletFilters = () => {
    setDraftFilters(emptyWalletFilters);
    setAppliedFilters(emptyWalletFilters);
    setAmountSortDirection("");
    setCurrentPage(1);
  };

  const toggleAmountSortDirection = () => {
    setAmountSortDirection((prev) => {
      if (prev === "ASC") return "DESC";
      if (prev === "DESC") return "";
      return "ASC";
    });
    setCurrentPage(1);
  };

  const toggleCreditSortDirection = () => {
    setCreditSortDirection((prev) => {
      if (prev === "ASC") return "DESC";
      if (prev === "DESC") return "";
      return "ASC";
    });
    setCurrentPage(1);
  };

  const toggleTransactionRow = (rowId) => {
    setSelectedTransactionRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((item) => item !== rowId)
        : [...prev, rowId],
    );
  };

  const toggleDisplayedTransactionRows = () => {
    if (allDisplayedTransactionsSelected) {
      setSelectedTransactionRows((prev) =>
        prev.filter((rowId) => !displayedTransactionRowIds.includes(rowId)),
      );
      return;
    }

    setSelectedTransactionRows((prev) => [
      ...new Set([...prev, ...displayedTransactionRowIds]),
    ]);
  };

  const toggleCreditRow = (rowId) => {
    setSelectedCreditRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((item) => item !== rowId)
        : [...prev, rowId],
    );
  };

  const toggleDisplayedCreditRows = () => {
    if (allDisplayedCreditsSelected) {
      setSelectedCreditRows((prev) =>
        prev.filter((rowId) => !displayedCreditRowIds.includes(rowId)),
      );
      return;
    }

    setSelectedCreditRows((prev) => [
      ...new Set([...prev, ...displayedCreditRowIds]),
    ]);
  };

  const escapePrintValue = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const getPrintableTransactionRows = () =>
    displayedTransactionRows
      .map((row, index) => ({
        row,
        rowId: getWalletTransactionRowId(row, index),
      }))
      .filter((item) => selectedTransactionRows.includes(item.rowId));

  const getPrintableCreditRows = () =>
    creditRows
      .map((row, index) => ({
        row,
        rowId: getWalletCreditRowId(row, index),
      }))
      .filter((item) => selectedCreditRows.includes(item.rowId));

  const handleExportTransactions = () => {
    if (!["transactions", "credits"].includes(activeTab)) {
      toast.error("Open Transactions or Credits to export rows.");
      return;
    }

    const isCreditsExport = activeTab === "credits";
    const selectedRows = isCreditsExport
      ? getPrintableCreditRows()
      : getPrintableTransactionRows();
    const exportTitle = isCreditsExport
      ? "Wallet Credits"
      : "Wallet Transactions";
    const exportLabel = isCreditsExport ? "credit row" : "transaction";

    if (!selectedRows.length) {
      toast.error(`Please select at least one ${exportLabel} to export.`);
      return;
    }

    const printWindow = window.open("", "_blank", "width=1000,height=800");

    if (!printWindow) return;

    const headers = isCreditsExport
      ? ["Date", "Purpose", "Description", "Credits Used"]
      : [
          "Date",
          "Reference ID",
          "Description",
          "Source",
          "Wallet Type",
          "Status",
          "Amount",
        ];
    const rowsHtml = selectedRows
      .map(({ row }) => {
        if (isCreditsExport) {
          const description = row?.orderReference
            ? `${row?.description || "-"} | Order: ${row.orderReference}`
            : row?.description || "-";

          return `
            <tr>
              <td>${escapePrintValue(formatWalletDate(row?.date))}</td>
              <td>${escapePrintValue(row?.purpose)}</td>
              <td>${escapePrintValue(description)}</td>
              <td>${escapePrintValue(row?.creditsUsed)}</td>
            </tr>
          `;
        }

        const amount = formatWalletAmount(
          getWalletRowValue(row, ["amount", "transactionAmount"], ""),
        );

        return `
          <tr>
            <td>${escapePrintValue(
              formatWalletDate(
                getWalletRowValue(row, [
                  "createdAt",
                  "date",
                  "transactionDate",
                ]),
              ),
            )}</td>
            <td>${escapePrintValue(
              getWalletRowValue(row, ["referenceId", "transactionId"]),
            )}</td>
            <td>${escapePrintValue(getWalletRowValue(row, ["description"]))}</td>
            <td>${escapePrintValue(getWalletRowValue(row, ["source"]))}</td>
            <td>${escapePrintValue(getWalletRowValue(row, ["walletType"]))}</td>
            <td>${escapePrintValue(
              getWalletRowValue(row, ["walletStatus", "status"]),
            )}</td>
            <td>${escapePrintValue(amount)}</td>
          </tr>
        `;
      })
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${exportTitle}</title>
          <style>
            body {
              margin: 0;
              padding: 24px;
              font-family: Inter, Arial, sans-serif;
              color: #344054;
              background: #ffffff;
            }

            .wallet-print-header {
              margin-bottom: 18px;
            }

            .wallet-print-title {
              margin: 0 0 6px;
              color: #101828;
              font-size: 22px;
              font-weight: 700;
            }

            .wallet-print-meta {
              color: #667085;
              font-size: 13px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }

            th,
            td {
              border: 1px solid #e4e7ec;
              padding: 10px;
              text-align: left;
              vertical-align: top;
            }

            th {
              background: #f8fafc;
              color: #475467;
              font-weight: 700;
            }

            @media print {
              body {
                padding: 12px;
              }
            }
          </style>
        </head>
        <body>
          <div class="wallet-print-header">
            <h1 class="wallet-print-title">${exportTitle}</h1>
            <div class="wallet-print-meta">
              ${selectedRows.length} selected ${exportLabel}${
                selectedRows.length === 1 ? "" : "s"
              }
            </div>
          </div>
          <table>
            <thead>
              <tr>
                ${headers
                  .map((header) => `<th>${escapePrintValue(header)}</th>`)
                  .join("")}
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const updateAddMethodForm = (field, value) => {
    setAddMethodForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const savePaymentMethod = async (event) => {
    event.preventDefault();
    const validationMessage = checkCreditCard({
      cardName: addMethodForm.nameOnCard,
      cardNumber: addMethodForm.cardNumber,
      expiry: addMethodForm.expiry,
      ccv: addMethodForm.cvv,
    });

    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    setIsSavingMethod(true);

    try {
      const jwt = userService.getUser();

      const addCardResponse = await CreditsController.AddUserCreditCard({
        Authorization: jwt,
        request: {
          cardName: addMethodForm.nameOnCard,
          cardNumber: addMethodForm.cardNumber,
          ccv: addMethodForm.cvv,
          companyId: userService.getCompID(),
          expiryDate: formatExpiryForApi(addMethodForm.expiry),
          isDefault,
          userId: currentUserId,
        },
      });

      if (!Utils.isSuccessResponse(addCardResponse)) {
        throw addCardResponse;
      }

      const cardsResponse = await CreditsController.getUserCreditCards({
        Authorization: jwt,
        userId: currentUserId,
      });

      const cards = Array.isArray(cardsResponse?.data)
        ? cardsResponse.data
        : [];
      setPaymentMethods(cards);
      const defaultCard = cards.find((card) => card.isDefault);
      setSelectedPaymentMethodId(
        defaultCard?.userCreditCardsId ||
          defaultCard?.id ||
          cards[0]?.userCreditCardsId ||
          cards[0]?.id ||
          "",
      );
      setAddMethodForm(emptyAddMethodForm);
      setIsDefault(false);
      setIsAddMethodOpen(false);
      if (resumeCreditPurchase) {
        setBuyCridits(true);
        setResumeCreditPurchase(false);
      }
      toast.success("Payment method added.");
    } catch (error) {
      console.error("Failed to save payment method", error);
      toast.error(
        error?.response?.data?.message || "Failed to save payment method.",
      );
    } finally {
      setIsSavingMethod(false);
    }
  };

  useEffect(() => {
    if (!isFilterOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isFilterOpen]);

  const convertToSqlTimestampFormat = (dateString) => {
    if (!dateString) {
      return null;
    }

    const [day, month, year] = dateString.split("/");

    if (!day || !month || !year) {
      return null;
    }

    return `${year}-${month}-${day} 00:00:00`;
  };
  useEffect(() => {
    if (activeTab !== "transactions") return undefined;

    const getWalletTransactions = async () => {
      setIsWalletLoading(true);

      try {
        const response = await CreditsController.getWalletTransactions({
          companyName: userService.getCompName(),
          dateFrom: convertToSqlTimestampFormat(dateRange.from),
          dateTo: convertToSqlTimestampFormat(dateRange.to),
          page: currentPage,
          sizePage: rowsPerPage,
          sortBy: amountSortDirection ? "amount" : "newest",
          sortDirection: amountSortDirection || "DESC",
          walletStatus: appliedFilters.walletStatus,
          walletType: appliedFilters.walletType.join(","),
          Authorization: userService.getUser(),
        });
        const walletData = response.data;
        const transactions = Array.isArray(walletData.transactions)
          ? walletData.transactions
          : [];

        setWalletRows(transactions);
        setWalletTotalRows(walletData.total ?? transactions.length);
        setWalletTotalPages(Math.max(walletData.totalPages || 1, 1));
      } catch (error) {
        console.error("Failed to load wallet transactions", error);
        setWalletRows([]);
        setWalletTotalRows(0);
        setWalletTotalPages(1);
      } finally {
        setIsWalletLoading(false);
      }
    };

    getWalletTransactions();
  }, [
    activeTab,
    appliedFilters,
    amountSortDirection,
    currentPage,
    dateRange.from,
    dateRange.to,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (activeTab !== "credits") return undefined;

    const getWalletCredits = async () => {
      setIsCreditLoading(true);

      try {
        const response = await CreditsController.getWalletCredits({
          page: currentPage,
          purpose: "",
          search,
          sizePage: rowsPerPage,
          sortDirection: creditSortDirection || "",
          sortBy: creditSortDirection ? "creditsUsed" : "newest",
          dateFrom: dateRange.from,
          dateTo: dateRange.to,
          companyId: userService.getCompID(),
          Authorization: userService.getUser(),
        });
        const creditData = response.data;
        const usageHistory = Array.isArray(creditData.usageHistory)
          ? creditData.usageHistory
          : [];

        setCreditRows(usageHistory);
        setCreditSummary({
          issuedThisMonth: creditData.issuedThisMonth ?? 0,
          revenueFromCredits: creditData.revenueFromCredits ?? 0,
          spentThisMonth: creditData.spentThisMonth ?? 0,
          total: creditData.total ?? usageHistory.length,
          totalActiveCredits: creditData.totalActiveCredits ?? 0,
          totalPages: Math.max(creditData.totalPages || 1, 1),
        });
      } catch (error) {
        console.error("Failed to load wallet credits", error);
        setCreditRows([]);
        setCreditSummary({
          issuedThisMonth: 0,
          revenueFromCredits: 0,
          spentThisMonth: 0,
          total: 0,
          totalActiveCredits: 0,
          totalPages: 1,
        });
      } finally {
        setIsCreditLoading(false);
      }
    };

    getWalletCredits();
  }, [
    activeTab,
    creditSortDirection,
    creditsRefreshKey,
    currentPage,
    dateRange.from,
    dateRange.to,
    rowsPerPage,
    search,
  ]);

  useEffect(() => {
    const getWalletOverview = async () => {
      setIsOverviewLoading(true);

      try {
        const response = await CreditsController.getWalletOverview({
          companyId: isRoot ? "" : userService.getCompID(),
          Authorization: userService.getUser(),
        });

        setWalletOverview({
          activeCredits: response?.data?.activeCredits ?? 0,
          pendingSettlements: response?.data?.pendingSettlements ?? 0,
          totalWalletBalance: response?.data?.totalWalletBalance ?? 0,
        });
      } catch (error) {
        console.error("Failed to load wallet overview", error);
        setWalletOverview({
          activeCredits: 0,
          pendingSettlements: 0,
          totalWalletBalance: 0,
        });
      } finally {
        setIsOverviewLoading(false);
      }
    };

    getWalletOverview();
  }, [isRoot]);

  useEffect(() => {
    if (activeTab !== "credits" || isRoot || !currentUserId) return undefined;

    const getPaymentMethods = async () => {
      setIsPaymentMethodsLoading(true);

      try {
        const response = await CreditsController.getUserCreditCards({
          Authorization: userService.getUser(),
          userId: currentUserId,
        });

        const cards = Array.isArray(response?.data) ? response.data : [];
        setPaymentMethods(cards);
        const defaultCard = cards.find((card) => card.isDefault);
        setSelectedPaymentMethodId(
          defaultCard?.userCreditCardsId ||
            defaultCard?.id ||
            cards[0]?.userCreditCardsId ||
            cards[0]?.id ||
            "",
        );
      } catch (error) {
        console.error("Failed to load payment methods", error);
        setPaymentMethods([]);
        setSelectedPaymentMethodId("");
      } finally {
        setIsPaymentMethodsLoading(false);
      }
    };

    getPaymentMethods();
  }, [activeTab, currentUserId, isRoot]);

  useEffect(() => {
    if (!buyCridits || isRoot) return undefined;

    const loadBillingProfile = async () => {
      setIsPreparingPayment(true);
      try {
        const companyResponse = await companyController.getCompanyDetails({
          Authorization: userService.getUser(),
          copmayRequest: {
            businessProfileID: userService.getBPID(),
            companyId: userService.getCompID(),
          },
        });

        const companyData = companyResponse?.data || {};
        const ownerName = String(
          getFirstValue(
            companyData.ownerName,
            parsedFirstName,
            userObject?.firstname,
            "Azalea User",
          ),
        ).trim();
        const ownerParts = ownerName.split(/\s+/).filter(Boolean);
        const firstName = getFirstValue(
          parsedFirstName,
          ownerParts[0],
          userObject?.firstname,
          "Azalea",
        );
        const lastName =
          getFirstValue(
            parsedLastName,
            ownerParts.slice(1).join(" "),
            userObject?.lastname,
            companyData.companyName,
            "User",
          ) || "User";

        const locality = getFirstValue(
          companyData.locality,
          companyData.city,
          companyData.route,
          companyData.countryName,
          "Beirut",
        );

        const address1 = getFirstValue(
          companyData.streetNumber && companyData.route
            ? `${companyData.streetNumber} ${companyData.route}`
            : null,
          companyData.route,
          companyData.address1,
          companyData.companyName,
          "Main Office",
        );

        setBillingProfile({
          firstName,
          lastName,
          email: getFirstValue(
            companyData.emailAddress,
            parsedUsername,
            userObject?.email,
            "billing@azalea.local",
          ),
          address1,
          locality,
          country: normalizeCountryCode(companyData.countryName),
          postalCode: getFirstValue(companyData.postalCode, "0000"),
          phoneNumber: `${getFirstValue(companyData.suffix, "")}${getFirstValue(
            companyData.phoneNumber,
            userObject?.phoneNumber,
            "",
          )}`,
        });
      } catch (error) {
        console.error("Failed to prepare billing profile", error);
        setBillingProfile({
          firstName: getFirstValue(parsedFirstName, "Azalea"),
          lastName: getFirstValue(parsedLastName, userObject?.lastname, "User"),
          email: getFirstValue(
            parsedUsername,
            userObject?.email,
            "billing@azalea.local",
          ),
          address1: "Main Office",
          locality: "Beirut",
          country: "LB",
          postalCode: "0000",
          phoneNumber: getFirstValue(userObject?.phoneNumber, ""),
        });
      } finally {
        setIsPreparingPayment(false);
      }
    };

    loadBillingProfile();
  }, [
    buyCridits,
    isRoot,
    parsedFirstName,
    parsedLastName,
    parsedUsername,
    userObject,
  ]);

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const livePlansResponse = await PromotionController.getAllBillingPlans({
          Authorization: userService.getUser(),
        });
        const livePlans = Array.isArray(livePlansResponse?.data)
          ? livePlansResponse.data
          : [];

        try {
          const response = await PromotionController.getPlansAndBilling({
            Authorization: userService.getUser(),
            companyId: userService.getCompID(),
          });
          const summaryPlans = Array.isArray(response?.data?.billingPlans)
            ? response.data.billingPlans
            : [];
          const sourceByName = new Map(
            livePlans
              .filter((plan) => plan?.planName)
              .map((plan) => [
                String(plan.planName).trim().toLowerCase(),
                plan,
              ]),
          );

          const mergedPlans = summaryPlans.map((plan) => {
            const sourcePlan = sourceByName.get(
              String(plan?.planName || "")
                .trim()
                .toLowerCase(),
            );

            return {
              ...sourcePlan,
              ...plan,
              billingPlanId:
                plan?.billingPlanId || sourcePlan?.billingPlanId || null,
              billingPlansDetails:
                (Array.isArray(plan?.billingPlansDetails) &&
                plan.billingPlansDetails.length
                  ? plan.billingPlansDetails
                  : null) ||
                (Array.isArray(sourcePlan?.billingPlansDetails) &&
                sourcePlan.billingPlansDetails.length
                  ? sourcePlan.billingPlansDetails
                  : null),
              features:
                (Array.isArray(plan?.features) && plan.features.length
                  ? plan.features
                  : null) ||
                (Array.isArray(sourcePlan?.features) &&
                sourcePlan.features.length
                  ? sourcePlan.features
                  : null),
            };
          });

          setPlans(mergedPlans.length ? mergedPlans : livePlans);
        } catch (error) {
          setPlans(livePlans);
        }

        setSelectedPlan(0);
      } catch (err) {
        console.error("Failed to load billing plans", err);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleBuyCredits = async () => {
    try {
      const selected = normalizedPlans?.[selectedPlan];

      if (!selected?.id) {
        toast.error("Please select a valid credit plan.");
        return;
      }

      const response = await PromotionController.buyCredits({
        Authorization: userService.getUser(),
        request: {
          billingPlanId: selected.id,
          companyId: userService.getCompID(),
          createdById: currentUserId,
        },
      });

      if (!Utils.isSuccessResponse(response)) {
        throw response;
      }

      if (response?.data) {
        setCreditSummary((prev) => ({
          ...prev,
          totalActiveCredits:
            response.data.availableCredits ?? prev.totalActiveCredits,
        }));
        setWalletOverview((prev) => ({
          ...prev,
          activeCredits: response.data.availableCredits ?? prev.activeCredits,
        }));
      }

      setCreditsRefreshKey((prev) => prev + 1);
      setBuyCridits(false);
      toast.success("Credits purchased successfully.");
    } catch (err) {
      console.error("Failed to buy credits", err);
      toast.error(err?.response?.data?.message || "Failed to buy credits.");
    }
  };

  const iconMap = {
    "Growth Boost": <StarterIcon />,
    "Sales Accelerator": <PopulariIcon />,
    "Max Revenue Engine": <ProIcon />,
  };

  const normalizedPlans = useMemo(() => {
    return plans.map((p) => ({
      id: p.billingPlanId,
      name: p.planName,
      credits: p.creditsIncluded,
      price: `$${Number(p.price || 0).toFixed(2)}`,
      rate:
        p.price && p.creditsIncluded
          ? `$${(Number(p.price) / Number(p.creditsIncluded)).toFixed(2)}/cr`
          : "$0.00/cr",
      badge: p.mostPopular ? "Most Popular" : null,
      icon: iconMap[p.planName] || <WalletIcon />,
      description: p.description,
    }));
  }, [plans]);

  const selected = normalizedPlans?.[selectedPlan];

  const Handlecurrency = (price) => {
    const currencyMap = {
      $: "840",
      "€": "EUR",
      "£": "GBP",
      "¥": "JPY",
    };

    const symbol = price.match(/[^\d.,\s]/)?.[0];

    return currencyMap[symbol] || "USD";
  };

  const Handletitalamount = (price) => {
    return Number(price.replace(/[^0-9.]/g, ""));
  };

  const HandleCriditAuthorize = async () => {
    const selectedMethod = paymentMethods.find(
      (method) =>
        String(method.userCreditCardsId || method.id) ===
        String(selectedPaymentMethodId),
    );

    if (!selected?.id) {
      toast.error("Please select a credit plan.");
      return;
    }

    if (!selectedMethod) {
      toast.error("Please select a saved payment method.");
      return;
    }

    if (!billingProfile) {
      toast.error("Billing profile is not ready yet.");
      return;
    }

    setIsProcessingCreditPayment(true);

    try {
      const authorizationResponse =
        await BlomBanckController.authorizeSavedCard({
          Authorization: userService.getUser(),
          request: {
            savedPaymentMethodId:
              selectedMethod.userCreditCardsId || selectedMethod.id,
            totalAmount: Number(selected.price.replace(/[^0-9.]/g, "")).toFixed(
              2,
            ),
            currency: "USD",
            referenceCode: `CREDITS-${selected.id}`,
            capture: true,
            firstName: billingProfile.firstName,
            lastName: billingProfile.lastName,
            email: billingProfile.email,
            address1: billingProfile.address1,
            locality: billingProfile.locality,
            country: billingProfile.country,
            postalCode: billingProfile.postalCode,
            phoneNumber: billingProfile.phoneNumber,
          },
        });

      const paymentStatus = String(
        authorizationResponse?.data?.status || "",
      ).toUpperCase();
      const paymentId = authorizationResponse?.data?.id;

      if (
        !paymentId ||
        ["INVALID_REQUEST", "DECLINED", "ERROR", "404", "NOT_FOUND"].includes(
          paymentStatus,
        )
      ) {
        throw new Error(
          authorizationResponse?.data?.status || "Card payment was declined.",
        );
      }

      await handleBuyCredits();
    } catch (error) {
      console.error("Failed to process card payment", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to process card payment.",
      );
    } finally {
      setIsProcessingCreditPayment(false);
    }
  };
  return (
    <div className="wallet-main-container">
      {ReqWD ? (
        <div className="ai-inhancment-popup-layer">
          <div className="request-withdrawal-view">
            {ReqWithdrow ? (
              <div className="request-withrow-data-in">
                <div className="request-withdrawal-page">
                  <div className="request-withdrawal-top">
                    <div>
                      <div className="request-withdrawal-title">
                        Request Withdrawal
                      </div>
                      <div className="request-withdrawal-subtitle">
                        Funds will be transferred to your linked account within
                        1-5 business days.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="request-withdrawal-close"
                      onClick={() => setReqWD(false)}
                      aria-label="Close request withdrawal"
                    >
                      <XIcon />
                    </button>
                  </div>

                  <div className="request-balance-row">
                    <div className="request-balance-card request-balance-card-available">
                      <div className="request-balance-label">
                        Available to withdraw
                      </div>
                      <div className="request-balance-value">$8,450.00</div>
                    </div>

                    <div className="request-balance-card request-balance-card-pending">
                      <div className="request-balance-label">
                        Pending (not available)
                      </div>
                      <div className="request-balance-value">$1,500.00</div>
                    </div>
                  </div>

                  <div className="request-field-group">
                    <label
                      className="request-label"
                      htmlFor="withdrawal-amount"
                    >
                      Withdrawal Amount
                    </label>
                    <div className="request-amount-box" id="withdrawal-amount">
                      <span>$</span> 0.00
                    </div>

                    <div className="request-percent-row">
                      <button type="button">25%</button>
                      <button type="button">50%</button>
                      <button type="button">75%</button>
                      <button type="button">Max</button>
                    </div>
                  </div>

                  <div className="request-field-group">
                    <div className="request-label">Withdraw To</div>

                    <button
                      type="button"
                      className="request-method-card request-method-card-selected"
                    >
                      <div className="request-method-left">
                        <span className="request-method-icon">
                          <ChaseBankIcon />
                        </span>
                        <div>
                          <div className="request-method-title">
                            Chase Bank ****4821
                          </div>
                          <div className="request-method-note">
                            Bank Account - Default
                          </div>
                        </div>
                      </div>

                      <div className="request-method-right">
                        <div className="request-method-time">
                          <span className="request-method-clock" /> 1-2 business
                          days
                        </div>
                        <span className="request-method-radio" />
                      </div>
                    </button>

                    <button type="button" className="request-method-card">
                      <div className="request-method-left">
                        <span className="request-method-icon">
                          <VisaIcon />
                        </span>
                        <div>
                          <div className="request-method-title">
                            Visa Business ****9341
                          </div>
                          <div className="request-method-note">
                            Credit Card - Expires 12/28
                          </div>
                        </div>
                      </div>

                      <div className="request-method-right">
                        <div className="request-method-time">
                          <span className="request-method-clock" /> 3-5 business
                          days
                        </div>
                        <span className="request-method-radio" />
                      </div>
                    </button>
                  </div>

                  <div className="request-field-group">
                    <label className="request-label" htmlFor="withdrawal-notes">
                      Notes <span>(optional)</span>
                    </label>
                    <textarea
                      id="withdrawal-notes"
                      className="request-notes"
                      placeholder="e.g. Q2 earnings withdrawal..."
                    />
                  </div>

                  <button
                    type="button"
                    className="request-submit"
                    onClick={() => setReqWithdrow(false)}
                  >
                    Enter a valid amount
                  </button>
                </div>
              </div>
            ) : (
              <div className="request-withrow-data-in request-withrow-confirm-wrap">
                <div className="request-confirm-page">
                  <div className="request-confirm-top">
                    <div className="request-confirm-top-new">
                      <div className="request-confirm-title">
                        Request Withdrawal
                      </div>
                      <div className="request-confirm-subtitle">
                        Funds will be transferred to your linked account within
                        1-5 business days.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="request-confirm-close"
                      onClick={() => {
                        setReqWD(false);
                        setReqWithdrow(true);
                      }}
                      aria-label="Close request withdrawal"
                    >
                      <XIcon />
                    </button>
                  </div>

                  <div className="request-confirm-balance-row">
                    <div className="request-confirm-balance-card">
                      <div className="request-confirm-balance-label">
                        Available to withdraw
                      </div>
                      <div className="request-confirm-balance-value">
                        $8,450.00
                      </div>
                    </div>

                    <div className="request-confirm-balance-card">
                      <div className="request-confirm-balance-label">
                        Pending (not available)
                      </div>
                      <div className="request-confirm-balance-value">
                        $1,500.00
                      </div>
                    </div>
                  </div>

                  <div className="request-confirm-field-group">
                    <div className="request-confirm-label">
                      Withdrawal Amount
                    </div>
                    <div className="request-confirm-amount-box">0.00</div>

                    <div className="request-confirm-percent-row">
                      <button type="button">25%</button>
                      <button type="button">50%</button>
                      <button type="button">75%</button>
                      <button type="button">Max</button>
                    </div>
                  </div>

                  <div className="request-confirm-field-group">
                    <div className="request-confirm-label">Withdraw To</div>

                    <button
                      type="button"
                      className="request-confirm-method-card"
                    >
                      <div className="request-confirm-method-left">
                        <ChaseBankIcon />
                        <div>
                          <div className="request-confirm-method-title">
                            Chase Bank ****4821
                          </div>
                          <div className="request-confirm-method-note">
                            Bank Account - Default
                          </div>
                        </div>
                      </div>

                      <div className="request-confirm-method-time">
                        1-2 business days
                      </div>
                    </button>

                    <button
                      type="button"
                      className="request-confirm-method-card"
                    >
                      <div className="request-confirm-method-left">
                        <VisaIcon />
                        <div>
                          <div className="request-confirm-method-title">
                            Visa Business ****9341
                          </div>
                          <div className="request-confirm-method-note">
                            Credit Card - Expires 12/28
                          </div>
                        </div>
                      </div>

                      <div className="request-confirm-method-time">
                        3-5 business days
                      </div>
                    </button>
                  </div>

                  <div className="request-confirm-field-group">
                    <div className="request-confirm-label">Notes</div>
                    <textarea className="request-confirm-notes" />
                  </div>

                  <button type="button" className="request-confirm-submit" />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {buyCridits ? (
        <div className="ai-inhancment-popup-layer">
          <div className="buy-credits-page">
            <div className="buy-credits-top">
              <div>
                <div className="buy-credits-title">Buy Azalea Credits</div>
                <div className="buy-credits-subtitle">
                  Credits are used for promotions, sponsored listings, and
                  priority placement.
                </div>
              </div>

              <div
                className="buy-credits-close"
                onClick={() => setBuyCridits(false)}
              >
                ×
              </div>
            </div>

            <div className="buy-credit-plans">
              {loading && (
                <div className="wallet-table-message">
                  Loading available plans...
                </div>
              )}
              {normalizedPlans.map((plan, index) => (
                <div
                  key={index}
                  className={`buy-credit-card ${
                    selectedPlan === index ? "buy-credit-card-active" : ""
                  }`}
                  onClick={() => {
                    if (normalizedPlans[index]) {
                      setSelectedPlan(index);
                    }
                  }}
                >
                  {plan.badge && (
                    <div
                      className={
                        plan.badge === "Best Value"
                          ? "buy-credit-badge-purple"
                          : "buy-credit-badge-blue"
                      }
                    >
                      {plan.badge}
                    </div>
                  )}

                  {selectedPlan === index && (
                    <div className="buy-credit-check">
                      <CheckIcon />
                    </div>
                  )}

                  <div className="buy-credit-icon">{plan.icon}</div>

                  <div className="buy-credit-name">{plan.name}</div>
                  <div className="buy-credit-amount">{plan.credits}</div>
                  <div className="buy-credit-note">credits</div>

                  <div className="buy-credit-bottom">
                    <div className="buy-credit-price">{plan.price}</div>
                    <div className="buy-credit-rate">{plan.rate}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="buy-credit-unlocks">
              <div className="buy-credit-unlocks-title">
                What credits unlock
              </div>

              <div className="buy-credit-unlocks-grid">
                <div className="buy-credit-unlock-item">
                  <div className="buy-credit-unlock-icon">
                    <ProductBoostIcon />
                  </div>
                  <div>
                    <div>Product Boost</div>
                    <span>50credits</span>
                  </div>
                </div>

                <div className="buy-credit-unlock-item">
                  <div className="buy-credit-unlock-icon">
                    <SponsoredAdIcon />
                  </div>
                  <div>
                    <div>Sponsored Ad</div>
                    <span>100credits</span>
                  </div>
                </div>

                <div className="buy-credit-unlock-item">
                  <div className="buy-credit-unlock-icon">
                    <PriorityListingIcon />
                  </div>
                  <div>
                    <div>Priority Listing</div>
                    <span>25credits</span>
                  </div>
                </div>

                <div className="buy-credit-unlock-item">
                  <div className="buy-credit-unlock-icon">
                    <HomePageFeatureIcon />
                  </div>
                  <div>
                    <div>Homepage Feature</div>
                    <span>150credits</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="wallet-billing-methods-card">
              <div className="wallet-billing-methods-header">
                <div>
                  <div className="wallet-usage-title">Payment Method</div>
                  <div className="wallet-credit-note">
                    Select a saved card to complete this credit purchase.
                  </div>
                </div>
              </div>

              <div className="wallet-payment-methods">
                {isPreparingPayment && (
                  <div className="wallet-table-message">
                    Preparing billing details...
                  </div>
                )}

                {!isPreparingPayment && paymentMethods.length === 0 && (
                  <div className="wallet-table-message">
                    Add a saved card before buying credits.
                    <div style={{ marginTop: "12px" }}>
                      <button
                        type="button"
                        className="wallet-add-method-button"
                        onClick={() => {
                          setResumeCreditPurchase(true);
                          setBuyCridits(false);
                          setIsAddMethodOpen(true);
                        }}
                      >
                        + Add Method
                      </button>
                    </div>
                  </div>
                )}

                {!isPreparingPayment &&
                  paymentMethods.map((method) => {
                    const methodId = method.userCreditCardsId || method.id;
                    const masked =
                      method.maskedNumber || maskCardNumber(method.cardNumber);
                    return (
                      <button
                        type="button"
                        key={methodId}
                        className={`wallet-payment-method-card ${
                          String(selectedPaymentMethodId) === String(methodId)
                            ? "buy-credit-card-active"
                            : ""
                        }`}
                        onClick={() => setSelectedPaymentMethodId(methodId)}
                      >
                        <div className="wallet-payment-method-icon">
                          <VisaIcon />
                        </div>
                        <div className="wallet-payment-method-info">
                          <div className="wallet-payment-method-title">
                            Card ending in {String(masked).slice(-4)}
                          </div>
                          <div className="wallet-credit-note">
                            {method.expiryDate
                              ? `Expires ${method.expiryDate}`
                              : "Saved card"}
                          </div>
                          <div className="wallet-payment-method-number">
                            {masked}
                          </div>
                        </div>
                        {method.isDefault && (
                          <div className="wallet-default-badge">DEFAULT</div>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {selected && (
              <button
                className="buy-credit-submit"
                onClick={HandleCriditAuthorize}
                disabled={
                  isProcessingCreditPayment ||
                  isPreparingPayment ||
                  paymentMethods.length === 0
                }
              >
                {isProcessingCreditPayment
                  ? "Processing payment..."
                  : `Continue - ${selected.credits} credits for ${selected.price}`}
              </button>
            )}
          </div>
        </div>
      ) : null}
      <div className="wallet-header">
        <div className="wallet-header-left">
          <div className="wallet-header-icon">
            <WalletIconBig stroke="#FFFFFF" />
          </div>

          <div className="wallet-header-text">
            <div className="wallet-header-title">Wallet</div>
            <div className="wallet-header-note">
              {isRoot
                ? "Platform-wide wallet ledger: vendor, driver, and admin wallet movements"
                : "Your store's balance, credits, and payment methods"}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`wallet-stats-section ${
          isRoot ? "wallet-stats-section-root" : "wallet-stats-section-vendor"
        }`}
      >
        <div className="wallet-stat-card">
          <div className="wallet-stat-inner">
            <div className="wallet-stat-card-top">
              <div className="wallet-stat-icon wallet-stat-icon-green">
                <TotalWalletBalanceIcon />
              </div>
              <div className="wallet-stat-badge wallet-stat-badge-green">
                Active
              </div>
            </div>
            <div className="wallet-stat-label">Total Wallets Balance</div>
            <div className="wallet-stat-value">
              {isOverviewLoading
                ? "..."
                : formatMoney(walletOverview.totalWalletBalance)}
            </div>
          </div>
        </div>

        <div className="wallet-stat-card">
          <div className="wallet-stat-inner">
            <div className="wallet-stat-card-top">
              <div className="wallet-stat-icon wallet-stat-icon-yellow">
                <PendingSettlementsIcon />
              </div>
              <div className="wallet-stat-dot"></div>
            </div>
            <div className="wallet-stat-label">Pending Settlements</div>
            <div className="wallet-stat-value">
              {isOverviewLoading
                ? "..."
                : formatMoney(walletOverview.pendingSettlements)}
            </div>
          </div>
        </div>

        <div className="wallet-stat-card">
          <div className="wallet-stat-inner">
            <div className="wallet-stat-card-top">
              <div className="wallet-stat-icon wallet-stat-icon-purple">
                <ActivePlatformCreditsIcon />
              </div>
              <div className="wallet-stat-small-text">Today</div>
            </div>
            <div className="wallet-stat-label">
              {isRoot ? "Active platform credits" : "Azalea credits"}
            </div>
            <div className="wallet-stat-value">
              {isOverviewLoading
                ? "..."
                : `${formatCreditsValue(walletOverview.activeCredits)} credits`}
            </div>
          </div>
        </div>
      </div>

      <div className="wallet-tabs">
        {/* Withdrawals and Billings are intentionally hidden for this flow. */}
        {["transactions", "credits"].map((tab) => (
          <div
            key={tab}
            className={`wallet-tab ${
              activeTab === tab ? "wallet-tab-active" : ""
            }`}
            onClick={() => {
              setActiveTab(tab);
              setCurrentPage(1);
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </div>
        ))}
      </div>

      {activeTab === "credits" && (
        <>
          <div className="wallet-credit-cards">
            {creditCards.map((card, index) => (
              <div className="wallet-credit-card" key={index}>
                <div className="wallet-credit-card-inner">
                  <div className="wallet-credit-label">{card[0]}</div>
                  <div className="wallet-credit-value">{card[1]}</div>
                  <div className="wallet-credit-note">{card[2]}</div>
                </div>
              </div>
            ))}

            {!isRoot && (
              <div
                className="wallet-request-withdrawal-card"
                onClick={() => setBuyCridits(true)}
              >
                <div className="wallet-request-withdrawal-btn">
                  + Buy Credits
                </div>
              </div>
            )}
          </div>

          {!isRoot && (
            <div className="wallet-billing-methods-card">
              <div className="wallet-billing-methods-header">
                <div>
                  <div className="wallet-usage-title">
                    Saved Payment Methods
                  </div>
                  <div className="wallet-credit-note">
                    Use saved cards for faster credit purchases.
                  </div>
                </div>

                <button
                  type="button"
                  className="wallet-add-method-button"
                  onClick={() => setIsAddMethodOpen(true)}
                >
                  + Add Method
                </button>
              </div>

              <div className="wallet-payment-methods">
                {isPaymentMethodsLoading && (
                  <div className="wallet-table-message">
                    Loading payment methods...
                  </div>
                )}

                {!isPaymentMethodsLoading && paymentMethods.length === 0 && (
                  <div className="wallet-table-message">
                    No payment methods found.
                  </div>
                )}

                {!isPaymentMethodsLoading &&
                  paymentMethods.map((method) => (
                    <div
                      className="wallet-payment-method-card"
                      key={method.userCreditCardsId || method.id}
                    >
                      <div className="wallet-payment-method-icon">
                        <VisaIcon />
                      </div>
                      <div className="wallet-payment-method-info">
                        <div className="wallet-payment-method-title">
                          Card ending in{" "}
                          {String(method.cardNumber || "").slice(-4)}
                        </div>
                        <div className="wallet-credit-note">
                          {method.expiryDate
                            ? `Expires ${method.expiryDate}`
                            : "Saved card"}
                        </div>
                        <div className="wallet-payment-method-number">
                          {maskCardNumber(method.cardNumber)}
                        </div>
                      </div>
                      {method.isDefault && (
                        <div className="wallet-default-badge">DEFAULT</div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="wallet-usage-title">Usage History</div>
        </>
      )}

      <div className="wallet-tools-row">
        <div className="wallet-tools-left">
          <div className="wallet-search">
            <SearchIcon />
            <input
              placeholder="Search by business ID..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div ref={filterPanelRef} className="wallet-filter-wrap">
            <button
              type="button"
              className="wallet-filter-button"
              onClick={() => {
                setDraftFilters(appliedFilters);
                setIsFilterOpen((prev) => !prev);
              }}
            >
              <FilterIcon />
              <span>Filter</span>
            </button>

            {isFilterOpen && (
              <div className="wallet-filter-panel">
                <div className="wallet-filter-panel-header">
                  <div className="wallet-filter-panel-title">Filter</div>
                  <button
                    type="button"
                    className="wallet-filter-close"
                    onClick={() => setIsFilterOpen(false)}
                    aria-label="Close wallet filters"
                  >
                    x
                  </button>
                </div>

                <div className="wallet-filter-section">
                  <div className="wallet-filter-section-title">
                    Wallet Type
                    <span className="wallet-filter-caret">^</span>
                  </div>

                  <div className="wallet-filter-checkbox-list">
                    {walletTypeOptions.map((option) => (
                      <label
                        className="wallet-filter-checkbox-row"
                        key={option.value}
                      >
                        <input
                          type="checkbox"
                          checked={draftFilters.walletType.includes(
                            option.value,
                          )}
                          onChange={() => toggleDraftWalletType(option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="wallet-filter-section">
                  <label
                    className="wallet-filter-section-title"
                    htmlFor="wallet-filter-status"
                  >
                    Status
                  </label>

                  <CustomDropdown
                    value={draftFilters.walletStatus}
                    options={walletStatusOptions}
                    placeholder="Select Status"
                    className="wallet-filter-select"
                    menuClassName="wallet-filter-select-menu"
                    optionClassName="wallet-filter-select-option"
                    onChange={(value) =>
                      setDraftFilters((prev) => ({
                        ...prev,
                        walletStatus: value,
                      }))
                    }
                    rightIcon={<ArrowDownIcon />}
                  />
                </div>

                <div className="wallet-filter-section">
                  <div className="wallet-filter-section-title">
                    Amount Order
                  </div>

                  <div className="wallet-filter-sort-options">
                    <button
                      type="button"
                      className={`wallet-filter-sort-option ${
                        amountSortDirection === "ASC"
                          ? "wallet-filter-sort-option-active"
                          : ""
                      }`}
                      onClick={() => {
                        setAmountSortDirection("ASC");
                        setCurrentPage(1);
                      }}
                    >
                      ↑ Increasing
                    </button>
                    <button
                      type="button"
                      className={`wallet-filter-sort-option ${
                        amountSortDirection === "DESC"
                          ? "wallet-filter-sort-option-active"
                          : ""
                      }`}
                      onClick={() => {
                        setAmountSortDirection("DESC");
                        setCurrentPage(1);
                      }}
                    >
                      ↓ Decreasing
                    </button>
                  </div>
                </div>

                <div className="wallet-filter-actions">
                  <button
                    type="button"
                    className="wallet-filter-clear"
                    onClick={clearWalletFilters}
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    className="wallet-filter-apply"
                    onClick={applyWalletFilters}
                  >
                    Apply All
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="wallet-tools-right">
          <DateRangePicker
            compact
            buttonLike
            buttonLabel="Date Range"
            className="payment-order-payment-date-picker"
            fromValue={dateRange.from}
            toValue={dateRange.to}
            onRangeChange={setDateRange}
            popoverClassName="payment-order-payment-date-popover"
          />

          <div
            className="wallet-export-button"
            onClick={handleExportTransactions}
          >
            <ExportIcon />
            <span>Export</span>
          </div>
        </div>
      </div>

      {activeTab === "transactions" && (
        <div className="wallet-table-container">
          <div className="wallet-list-table">
            <div className="wallet-list-header">
              <div className="wallet-col-check">
                <input
                  type="checkbox"
                  checked={allDisplayedTransactionsSelected}
                  onChange={toggleDisplayedTransactionRows}
                />
              </div>
              <div className="wallet-col-date-header">Date</div>
              <div className="wallet-col-ref-header">Reference ID</div>
              <div className="wallet-col-desc-header">Description</div>
              <div className="wallet-col-source-header">Source</div>
              <div className="wallet-col-type-header">Wallet Type</div>
              <div className="wallet-col-status-header">Status</div>
              <button
                type="button"
                className="wallet-col-amount-header wallet-amount-sort-button"
                onClick={toggleAmountSortDirection}
              >
                Amount
                <span>
                  {amountSortDirection === "ASC"
                    ? "↑"
                    : amountSortDirection === "DESC"
                      ? "↓"
                      : "Sort"}
                </span>
              </button>
            </div>

            {isWalletLoading && (
              <div className="wallet-table-message">Loading wallet data...</div>
            )}

            {!isWalletLoading && displayedTransactionRows.length === 0 && (
              <div className="wallet-table-message">
                No wallet transactions found.
              </div>
            )}

            {!isWalletLoading &&
              displayedTransactionRows.map((row, index) => {
                const rowId = getWalletTransactionRowId(row, index);
                const amount = formatWalletAmount(
                  getWalletRowValue(row, ["amount", "transactionAmount"], ""),
                );
                const status = getWalletRowValue(row, [
                  "walletStatus",
                  "status",
                ]);
                const normalizedStatus = String(status).toLowerCase();

                return (
                  <div className="wallet-list-row" key={rowId}>
                    <div className="wallet-col-check">
                      <input
                        type="checkbox"
                        checked={selectedTransactionRows.includes(rowId)}
                        onChange={() => toggleTransactionRow(rowId)}
                      />
                    </div>
                    <div className="wallet-col-date">
                      {formatWalletDate(
                        getWalletRowValue(row, [
                          "createdAt",
                          "date",
                          "transactionDate",
                        ]),
                      )}
                    </div>
                    <div className="wallet-col-ref">
                      {getWalletRowValue(row, ["referenceId", "transactionId"])}
                    </div>
                    <div className="wallet-col-desc">
                      {getWalletRowValue(row, ["description"])}
                    </div>
                    <div className="wallet-col-source">
                      <span className="wallet-source-badge">
                        {getWalletRowValue(row, ["source"])}
                      </span>
                    </div>
                    <div className="wallet-col-type">
                      {getWalletRowValue(row, ["walletType"])}
                    </div>
                    <div className="wallet-col-status">
                      <span
                        className={
                          normalizedStatus === "completed"
                            ? "wallet-status-completed"
                            : normalizedStatus === "approved"
                              ? "wallet-status-approved"
                              : normalizedStatus === "rejected"
                                ? "wallet-status-rejected"
                                : "wallet-status-pending"
                        }
                      >
                        {status}
                      </span>
                    </div>
                    <div
                      className={
                        String(amount).startsWith("-")
                          ? "wallet-amount-minus"
                          : "wallet-amount-plus"
                      }
                    >
                      {amount}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {activeTab === "credits" && (
        <div className="wallet-credit-table">
          <div className="wallet-credit-header">
            <div>
              <input
                type="checkbox"
                checked={allDisplayedCreditsSelected}
                onChange={toggleDisplayedCreditRows}
              />
            </div>
            <div>Date</div>
            <div>Purpose</div>
            <div>Description</div>
            <button
              type="button"
              className="wallet-credit-sort-button"
              onClick={toggleCreditSortDirection}
            >
              Credits Used
              <span>
                {creditSortDirection === "ASC"
                  ? "↑"
                  : creditSortDirection === "DESC"
                    ? "↓"
                    : "Sort"}
              </span>
            </button>
          </div>

          {isCreditLoading && (
            <div className="wallet-table-message">Loading credit data...</div>
          )}

          {!isCreditLoading && creditRows.length === 0 && (
            <div className="wallet-table-message">
              No credit usage history found.
            </div>
          )}

          {!isCreditLoading &&
            creditRows.map((row, index) => {
              const rowId = getWalletCreditRowId(row, index);

              return (
                <div className="wallet-credit-row" key={rowId}>
                  <div>
                    <input
                      type="checkbox"
                      checked={selectedCreditRows.includes(rowId)}
                      onChange={() => toggleCreditRow(rowId)}
                    />
                  </div>
                  <div>{formatWalletDate(row.date)}</div>
                  <div>
                    <span className="wallet-source-badge">{row.purpose}</span>
                  </div>
                  <div>
                    <div>{row.description}</div>
                    {row.orderReference && (
                      <div className="wallet-credit-subtext">
                        Order: {row.orderReference}
                      </div>
                    )}
                  </div>
                  <div>{row.creditsUsed}</div>
                </div>
              );
            })}
        </div>
      )}

      {activeTab === "withdrawals" && (
        <>
          <div className="wallet-withdrawal-summary-cards">
            {withdrawalCards.map((card, index) => (
              <div className="wallet-withdrawal-summary-card" key={index}>
                <div className="wallet-withdrawal-summary-inner">
                  <div className="wallet-withdrawal-summary-label">
                    {card[0]}
                  </div>

                  <div className="wallet-withdrawal-summary-value">
                    {card[1]}
                  </div>

                  <div className="wallet-withdrawal-summary-note">
                    {card[2]}
                  </div>
                </div>
              </div>
            ))}

            {!isRoot && (
              <div
                className="wallet-request-withdrawal-card"
                onClick={() => setReqWD(true)}
              >
                <div className="wallet-request-withdrawal-btn">
                  Request Withdrawal
                </div>
              </div>
            )}
          </div>

          <div className="wallet-usage-title">Withdrawal Requests</div>

          <div className="wallet-withdrawal-table">
            <div className="wallet-withdrawal-header">
              <div>
                <input type="checkbox" />
              </div>

              <div>Date Requested</div>
              <div>Request ID</div>
              <div>Business</div>
              <div>Destination</div>
              <div>Status</div>
              <div>Notes</div>
              <div>Amount</div>

              {isRoot && <div>Actions</div>}
            </div>

            {isWithdrawalLoading && (
              <div className="wallet-table-message">
                Loading withdrawal requests...
              </div>
            )}

            {!isWithdrawalLoading && withdrawalRows.length === 0 && (
              <div className="wallet-table-message">
                No withdrawal requests found.
              </div>
            )}

            {!isWithdrawalLoading &&
              withdrawalRows.map((row, index) => {
                const status = row.status || "-";
                const normalizedStatus = status.toLowerCase();
                const ibanLastFour = row.iban ? row.iban.slice(-4) : "";

                return (
                  <div
                    className="wallet-withdrawal-row"
                    key={row.walletTransactionId ?? row.requestId ?? index}
                  >
                    <div>
                      <input type="checkbox" />
                    </div>

                    <div>{formatWalletDate(row.dateRequested)}</div>

                    <div>{row.requestId}</div>

                    <div>
                      <span className="wallet-source-badge">
                        {row.businessType}
                      </span>

                      <span className="wallet-business-name">
                        {row.businessName}
                      </span>
                    </div>

                    <div className="wallet-destination">
                      <ChaseBankIcon />

                      <div className="wallet-destination-text">
                        <div>{row.bankName || row.destination}</div>

                        <div className="wallet-destination-sub">
                          {ibanLastFour ? `**** ${ibanLastFour}` : row.iban}
                        </div>
                      </div>
                    </div>

                    <div>
                      <span
                        className={
                          normalizedStatus === "approved"
                            ? "wallet-status-approved"
                            : normalizedStatus === "rejected"
                              ? "wallet-status-rejected"
                              : normalizedStatus === "completed"
                                ? "wallet-status-completed"
                                : "wallet-status-pending"
                        }
                      >
                        {status}
                      </span>
                    </div>

                    <div>{row.notes || "-"}</div>

                    <div>{formatWalletAmount(row.amount)}</div>

                    {isRoot && (
                      <div className="wallet-actions">
                        <span className="wallet-action-approve">✓</span>

                        <span className="wallet-action-reject">×</span>
                      </div>
                    )}
                  </div>
                );
              })}

            {false &&
              withdrawalRows.map((row, index) => (
                <div className="wallet-withdrawal-row" key={index}>
                  <div>
                    <input type="checkbox" />
                  </div>

                  <div>Dec 5, 2025 - 11:57 PM</div>

                  <div>{row[0]}</div>

                  <div>
                    <span className="wallet-source-badge">{row[1]}</span>

                    <span className="wallet-business-name">{row[2]}</span>
                  </div>

                  <div className="wallet-destination">
                    <ChaseBankIcon />

                    <div className="wallet-destination-text">
                      <div>Chase Bank</div>

                      <div className="wallet-destination-sub">**** 4821</div>
                    </div>
                  </div>

                  <div>
                    <span
                      className={
                        row[4] === "Approved"
                          ? "wallet-status-approved"
                          : row[4] === "Rejected"
                            ? "wallet-status-rejected"
                            : "wallet-status-pending"
                      }
                    >
                      {row[4]}
                    </span>
                  </div>

                  <div>{row[5]}</div>

                  <div>{row[6]}</div>

                  {isRoot && (
                    <div className="wallet-actions">
                      <span className="wallet-action-approve">✓</span>

                      <span className="wallet-action-reject">×</span>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      )}

      {activeTab === "billings" && (
        <>
          <div className="wallet-billing-methods-card">
            <div className="wallet-billing-methods-header">
              <div>
                <div className="wallet-usage-title">Saved Payment Methods</div>
                <div className="wallet-credit-note">
                  Manage accounts for withdrawals and purchasing credits.
                </div>
              </div>

              {!isRoot && (
                <button
                  type="button"
                  className="wallet-add-method-button"
                  onClick={() => setIsAddMethodOpen(true)}
                >
                  + Add Method
                </button>
              )}
            </div>

            <div className="wallet-payment-methods">
              {isBillingLoading && (
                <div className="wallet-table-message">
                  Loading payment methods...
                </div>
              )}

              {!isBillingLoading && paymentMethods.length === 0 && (
                <div className="wallet-table-message">
                  No payment methods found.
                </div>
              )}

              {!isBillingLoading &&
                paymentMethods.map((method) => (
                  <div className="wallet-payment-method-card" key={method.id}>
                    <div className="wallet-payment-method-icon">
                      {method.cardName?.toLowerCase().includes("bank") ? (
                        <ChaseBankIcon />
                      ) : (
                        <VisaIcon />
                      )}
                    </div>
                    <div className="wallet-payment-method-info">
                      <div className="wallet-payment-method-title">
                        {method.cardName}
                      </div>
                      <div className="wallet-credit-note">Payment Method</div>
                      <div className="wallet-payment-method-number">
                        {method.maskedNumber}
                      </div>
                    </div>
                    {method.isDefault && (
                      <div className="wallet-default-badge">DEFAULT</div>
                    )}
                  </div>
                ))}

              {false && (
                <div className="wallet-payment-method-card">
                  <div className="wallet-payment-method-icon">
                    <ChaseBankIcon />
                  </div>
                  <div className="wallet-payment-method-info">
                    <div className="wallet-payment-method-title">
                      Chase Business Checking
                    </div>
                    <div className="wallet-credit-note">Bank Account</div>
                    <div className="wallet-payment-method-number">
                      •••• 4821
                    </div>
                  </div>
                  <div className="wallet-default-badge">DEFAULT</div>
                </div>
              )}

              {false && (
                <div className="wallet-payment-method-card">
                  <div className="wallet-payment-method-icon">
                    <VisaIcon />
                  </div>
                  <div className="wallet-payment-method-info">
                    <div className="wallet-payment-method-title">
                      Visa Business Card
                    </div>
                    <div className="wallet-credit-note">Card</div>
                    <div className="wallet-payment-method-number">
                      •••• 9341
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="wallet-usage-title">Billing History</div>

          <div className="wallet-billing-table">
            <div className="wallet-billing-header">
              <div>
                <input type="checkbox" />
              </div>
              <div>Date</div>
              <div>Billing Item</div>
              <div>Destination</div>
              <div>Status</div>
              <div>Amount</div>
            </div>

            {isBillingLoading && (
              <div className="wallet-table-message">
                Loading billing history...
              </div>
            )}

            {!isBillingLoading && billingRows.length === 0 && (
              <div className="wallet-table-message">
                No billing history found.
              </div>
            )}

            {!isBillingLoading &&
              billingRows.map((row, index) => {
                const status = row.status || "-";
                const normalizedStatus = status.toLowerCase();

                return (
                  <div
                    className="wallet-billing-row"
                    key={row.billingId ?? index}
                  >
                    <div>
                      <input type="checkbox" />
                    </div>
                    <div>{formatWalletDate(row.date)}</div>
                    <div className="wallet-col-ref">{row.billingItem}</div>
                    <div className="wallet-destination">
                      <VisaIcon />

                      <div className="wallet-destination-text">
                        <div>{row.destination}</div>
                      </div>
                    </div>
                    <div>
                      <span
                        className={
                          normalizedStatus === "completed"
                            ? "wallet-status-completed"
                            : normalizedStatus === "approved"
                              ? "wallet-status-approved"
                              : normalizedStatus === "rejected"
                                ? "wallet-status-rejected"
                                : "wallet-status-pending"
                        }
                      >
                        {status}
                      </span>
                    </div>
                    <div>{formatWalletAmount(row.amount)}</div>
                  </div>
                );
              })}

            {false &&
              billingRows.map((row, index) => (
                <div className="wallet-billing-row" key={index}>
                  <div>
                    <input type="checkbox" />
                  </div>
                  <div>Dec 5, 2025 - 11:57 PM</div>
                  <div className="wallet-col-ref">{row[0]}</div>
                  <div className="wallet-destination">
                    {row[1].includes("Chase") ? (
                      <ChaseBankIcon />
                    ) : (
                      <VisaIcon />
                    )}

                    <div className="wallet-destination-text">
                      <div>{row[1].split("****")[0]}</div>
                      <div className="wallet-destination-sub">
                        ****{row[1].split("****")[1]}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className="wallet-status-completed">{row[2]}</span>
                  </div>
                  <div>{row[3]}</div>
                </div>
              ))}
          </div>
        </>
      )}

      <div className="wallet-footer">
        <div className="wallet-rows-count">
          <span>Rows per page</span>

          <div className="rows-pill">
            <span>10</span>
            <ArrowDownIcon />
          </div>

          <span>of {activeTotalRows || 0} rows</span>
        </div>

        <div className="wallet-pagination">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          >
            <ArrowBackIcon />
          </button>
          {walletPageNumbers.map((pageNumber) => (
            <button
              type="button"
              className={currentPage === pageNumber ? "wallet-page-active" : ""}
              key={pageNumber}
              onClick={() => setCurrentPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}
          <button
            type="button"
            disabled={currentPage >= activeTotalPages}
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, activeTotalPages))
            }
          >
            <ArrowFront />
          </button>
          <div>‹</div>
          <div className="wallet-page-active">1</div>
          <div>2</div>
          <div>3</div>
          <div>...</div>
          <div>10</div>
          <div>›</div>
        </div>
      </div>

      {isAddMethodOpen && (
        <div
          className="wallet-add-method-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setResumeCreditPurchase(false);
              setIsAddMethodOpen(false);
            }
          }}
        >
          <div className="payment-deyails-card">
            <div className="payment-deyails-card-header">
              <div className="payment-deyails-card-header-title">
                Pay by Card
              </div>
              <div className="payment-deyails-card-header-text">
                Secured by Areeba. Your bank may request a one-time verification
                code.
              </div>
            </div>
            <div className="payment-deyails-card-body">
              <div className="payment-deyails-card-body-input">
                <div className="payment-deyails-card-body-input-title">
                  Cardholder Full Name
                </div>
                <input
                  className="payment-deyails-card-body-input-data"
                  placeholder="Sarah Mitchell"
                  value={addMethodForm.nameOnCard}
                  onChange={(e) =>
                    updateAddMethodForm("nameOnCard", e.target.value)
                  }
                ></input>
              </div>
              <div className="payment-deyails-card-body-input">
                <div className="payment-deyails-card-body-input-title">
                  Card Number
                </div>
                <input
                  className="payment-deyails-card-body-input-data"
                  placeholder="4111  1111  1111  111"
                  value={addMethodForm.cardNumber
                    .replace(/(.{4})/g, "$1 ")
                    .trim()}
                  onChange={(e) =>
                    updateAddMethodForm(
                      "cardNumber",
                      e.target.value.replace(/\D/g, "").slice(0, 16),
                    )
                  }
                ></input>
              </div>
              <div className="payment-deyails-card-body-details">
                <div className="payment-deyails-card-body-details-data">
                  <div className="payment-deyails-card-body-details-data-input">
                    <div className="payment-deyails-card-body-details-data-input-title">
                      Expiry
                    </div>
                    <input
                      className="payment-deyails-card-body-details-data-input-datain"
                      placeholder="MM/YY"
                      value={addMethodForm.expiry}
                      maxLength={5}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw.length < addMethodForm.expiry.length) {
                          updateAddMethodForm(
                            "expiry",
                            raw.replace("/", "").length < 2
                              ? raw.replace("/", "")
                              : raw,
                          );
                          return;
                        }
                        const digits = raw.replace(/[^\d]/g, "");
                        if (digits.length === 0) {
                          updateAddMethodForm("expiry", "");
                          return;
                        }
                        if (digits.length === 1) {
                          if (parseInt(digits, 10) >= 2) {
                            updateAddMethodForm("expiry", "0" + digits + "/");
                          } else {
                            updateAddMethodForm("expiry", digits);
                          }
                          return;
                        }
                        let monthNum = parseInt(digits.substring(0, 2), 10);
                        if (monthNum > 12) monthNum = 12;
                        if (monthNum < 1) monthNum = 1;
                        const month = String(monthNum).padStart(2, "0");
                        const year = digits.substring(2, 4);
                        updateAddMethodForm("expiry", month + "/" + year);
                      }}
                    ></input>
                  </div>
                  <div className="payment-deyails-card-body-details-data-input">
                    <div className="payment-deyails-card-body-details-data-input-title">
                      CVV
                    </div>
                    <input
                      className="payment-deyails-card-body-details-data-input-datain"
                      placeholder="CVV"
                      value={addMethodForm.cvv}
                      onChange={(e) => {
                        updateAddMethodForm(
                          "cvv",
                          e.target.value.replace(/\D/g, "").slice(0, 4),
                        );
                      }}
                    />
                  </div>
                </div>
                <div className="payment-deyails-card-body-details-checkbox">
                  <input
                    type="checkbox"
                    className="payment-deyails-card-body-details-checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  ></input>
                  <div className="payment-deyails-card-body-details-checkbox">
                    Save card for future payments.
                  </div>
                </div>
              </div>
            </div>
            <div
              className="payment-deyails-card-footer"
              onClick={savePaymentMethod}
            >
              {isSavingMethod ? "Saving..." : "Save & Continue"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Wallet;
